import apiRoutes from '@constants/apiRoutes';
import baseApi from '@services/api';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_NUMBER, PayoutStatus } from '@constants/constants';
import {
  CommissionChartDownloadResponse,
  DownloadActiveCohortsResponse,
  DownloadCommissionsResponse,
  DownloadPayoutsResponse,
  GetActiveCohortsResponse,
  GetCommissionsChartResponse,
  GetCommissionsFiltersResponse,
  GetCommissionsResponse,
  GetFinalCommissionsResponse,
  GetPayoutsResponse,
  PayoutStatusUpdatePayload,
} from './types/commissions';

const commissions = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /* REFRESH COMMISSIONS */
    refreshCommissions: builder.mutation<void, void>({
      query: () => ({
        url: apiRoutes.refreshCommissions,
        method: 'POST',
      }),
    }),

    /* GET COMMISSIONS */
    getCommissions: builder.query<
      GetCommissionsResponse,
      {
        pageNumber: number;
        pageSize: number;
        referrerId?: string;
      }
    >({
      query: ({ pageNumber, pageSize, referrerId }) => {
        const qps = [];

        qps.push(`pageNumber=${pageNumber || DEFAULT_PAGE_NUMBER}`);
        qps.push(`pageSize=${pageSize || DEFAULT_PAGE_SIZE}`);
        if (referrerId) qps.push(`referrerId=${referrerId}`);

        return apiRoutes.commissions(qps.join('&'));
      },
    }),

    /* DOWNLOAD COMMISSIONS */
    downloadCommissions: builder.query<
      DownloadCommissionsResponse,
      {
        referrerId?: string;
      }
    >({
      query: ({ referrerId }) => {
        const qps = [];

        if (referrerId) qps.push(`referrerId=${referrerId}`);

        return apiRoutes.commissionsDownload(qps.join('&'));
      },
    }),

    /* GET ACTIVE COHORTS */
    getActiveCohorts: builder.query<
      GetActiveCohortsResponse,
      {
        pageNumber: number;
        pageSize: number;
        referrerId?: string;
      }
    >({
      query: ({ pageNumber, pageSize, referrerId }) => {
        const qps = [];

        qps.push(`pageNumber=${pageNumber || DEFAULT_PAGE_NUMBER}`);
        qps.push(`pageSize=${pageSize || DEFAULT_PAGE_SIZE}`);
        if (referrerId) qps.push(`referrerId=${referrerId}`);

        return apiRoutes.activeCohorts(qps.join('&'));
      },
    }),

    /* DOWNLOAD ACTIVE COHORTS */
    downloadActiveCohorts: builder.query<
      DownloadActiveCohortsResponse,
      {
        referrerId?: string;
      }
    >({
      query: ({ referrerId }) => {
        const qps = [];

        if (referrerId) qps.push(`referrerId=${referrerId}`);

        return apiRoutes.activeCohortsDownload(qps.join('&'));
      },
    }),

    /* GET PAYOUTS */
    getPayouts: builder.query<
      GetPayoutsResponse,
      {
        pageNumber: number;
        pageSize: number;
        referrerId?: string;
        status?: PayoutStatus[];
      }
    >({
      query: ({ pageNumber, pageSize, referrerId, status }) => {
        const qps = [];

        qps.push(`pageNumber=${pageNumber || DEFAULT_PAGE_NUMBER}`);
        qps.push(`pageSize=${pageSize || DEFAULT_PAGE_SIZE}`);

        if (referrerId) qps.push(`referrerId=${referrerId}`);
        if (status && status.length > 0) qps.push(`payoutStatus=${status.join(',')}`);

        return apiRoutes.payouts(qps.join('&'));
      },
    }),

    /* DOWNLOAD PAYOUTS */
    downloadPayouts: builder.query<
      DownloadPayoutsResponse,
      {
        referrerId?: string;
        status?: PayoutStatus[];
      }
    >({
      query: ({ referrerId, status }) => {
        const qps = [];

        if (referrerId) qps.push(`referrerId=${referrerId}`);
        if (status && status.length > 0) qps.push(`payoutStatus=${status.join(',')}`);

        return apiRoutes.payoutsDownload(qps.join('&'));
      },
    }),

    /* GET CHARTS */
    getCommissionsChart: builder.query<
      GetCommissionsChartResponse,
      {
        referrerId?: string;
        payoutMonths?: string[];
      }
    >({
      query: ({ referrerId, payoutMonths }) => {
        const qps = [];

        if (referrerId) qps.push(`referrerId=${referrerId}`);
        if (payoutMonths?.length) qps.push(`payoutMonths=${payoutMonths.join(',')}`);

        return apiRoutes.commissionsChart(qps.join('&'));
      },
    }),

    /* MARK ACTIVE COHORT AS DEFAULTED */
    markActiveCohortAsDefaulted: builder.mutation<void, string>({
      query: (cohortDetailsId: string) => ({
        url: `${apiRoutes.markActiveCohortAsDefaulted(cohortDetailsId)}`,
        method: 'POST',
      }),
    }),

    /* GET COMMISSIONS FILTERS */
    getCommissionsFilters: builder.query<GetCommissionsFiltersResponse, void>({
      query: () => apiRoutes.commissionsFilters,
    }),

    /* UPDATE PAYOUT STATUS */
    updatePayoutStatus: builder.mutation<
      void,
      {
        id: string;
        payload: PayoutStatusUpdatePayload;
      }
    >({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.updatePayoutStatus(id)}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    /* GET FINAL COMMISSIONS */
    getFinalCommissions: builder.query<GetFinalCommissionsResponse, string>({
      query: (payoutId) => apiRoutes.finalCommissions(payoutId),
    }),

    /* DOWNLOAD COMMISSIONS CHARTS */
    commissionsChartsDownload: builder.query<
      CommissionChartDownloadResponse,
      { referrerId?: string; payoutMonths?: string[] }
    >({
      query: ({ referrerId, payoutMonths }) => {
        const qps = [];

        if (referrerId) qps.push(`referrerId=${referrerId}`);
        if (payoutMonths?.length) qps.push(`payoutMonths=${payoutMonths.join(',')}`);

        return apiRoutes.commissionsChartsDownload(qps.join('&'));
      },
    }),
  }),
});

export const {
  useRefreshCommissionsMutation,
  useLazyGetActiveCohortsQuery,
  useLazyGetCommissionsQuery,
  useLazyGetCommissionsChartQuery,
  useMarkActiveCohortAsDefaultedMutation,
  useLazyGetCommissionsFiltersQuery,
  useLazyGetPayoutsQuery,
  useLazyDownloadCommissionsQuery,
  useLazyDownloadActiveCohortsQuery,
  useUpdatePayoutStatusMutation,
  useLazyDownloadPayoutsQuery,
  useLazyGetFinalCommissionsQuery,
  useLazyCommissionsChartsDownloadQuery,
} = commissions;
